import React from "react";
import { LANDING } from "../constants/testIds/landing";
import { Heart, Music, Sparkles } from "lucide-react";

export const Philosophy = ({ t }) => {
  return (
    <section
      id="philosophy"
      className="py-24 sm:py-32 lg:py-40 bg-[#efe7dd] text-[#1c110e] border-t border-[#b58e70]/30"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        
        {/* Section Header (Offset and Asymmetrical) */}
        <div className="md:grid md:grid-cols-12 md:gap-8 mb-20 lg:mb-28">
          <div className="md:col-span-8 space-y-4">
            <span className="font-sans text-xs sm:text-sm tracking-[0.3em] uppercase text-[#d97036] font-medium block">
              {t.philosophy.title}
            </span>
            <h2
              data-testid={LANDING.philosophyTitle}
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1c110e] tracking-tight font-light leading-tight"
            >
              {t.philosophy.subtitle}
            </h2>
          </div>
          <div className="md:col-span-4 flex items-end mt-4 md:mt-0">
            <p className="font-sans text-sm text-[#5a4b46] leading-relaxed font-light border-l border-[#b58e70]/40 pl-6">
              {t.philosophy.intro}
            </p>
          </div>
        </div>

        {/* 3-Column Staggered Layout (High Negative Space, Sharp Borders) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 items-stretch">
          {/* Card 1: Connection */}
          <div
            data-testid={LANDING.philosophyCardConnection}
            className="group relative bg-[#e5d8cb] p-8 lg:p-12 border border-[#b58e70]/20 hover:border-[#b58e70]/60 transition-all duration-500 flex flex-col justify-between"
          >
            <div className="space-y-6">
              <div className="w-12 h-12 bg-[#8a2424]/10 flex items-center justify-center border border-[#8a2424]/20 group-hover:bg-[#8a2424]/20 group-hover:border-[#8a2424]/40 transition-all duration-500">
                <Heart className="w-5 h-5 text-[#d97036]" />
              </div>
              <h3 className="font-serif text-2xl lg:text-3xl text-[#1c110e] font-light">
                {t.philosophy.connectionTitle}
              </h3>
              <p className="font-sans text-sm lg:text-base text-[#5a4b46] leading-relaxed font-light">
                {t.philosophy.connectionText}
              </p>
            </div>
            {/* Elegant corner accent */}
            <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-transparent group-hover:border-[#d97036] transition-all duration-300" />
          </div>

          {/* Card 2: Musicality (Offset visually down on larger screens) */}
          <div
            data-testid={LANDING.philosophyCardMusicality}
            className="group relative bg-[#e5d8cb] p-8 lg:p-12 border border-[#b58e70]/20 hover:border-[#b58e70]/60 transition-all duration-500 flex flex-col justify-between md:translate-y-6"
          >
            <div className="space-y-6">
              <div className="w-12 h-12 bg-[#8a2424]/10 flex items-center justify-center border border-[#8a2424]/20 group-hover:bg-[#8a2424]/20 group-hover:border-[#8a2424]/40 transition-all duration-500">
                <Music className="w-5 h-5 text-[#d97036]" />
              </div>
              <h3 className="font-serif text-2xl lg:text-3xl text-[#1c110e] font-light">
                {t.philosophy.musicalityTitle}
              </h3>
              <p className="font-sans text-sm lg:text-base text-[#5a4b46] leading-relaxed font-light">
                {t.philosophy.musicalityText}
              </p>
            </div>
            <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-transparent group-hover:border-[#d97036] transition-all duration-300" />
          </div>

          {/* Card 3: Technique */}
          <div
            data-testid={LANDING.philosophyCardTechnique}
            className="group relative bg-[#e5d8cb] p-8 lg:p-12 border border-[#b58e70]/20 hover:border-[#b58e70]/60 transition-all duration-500 flex flex-col justify-between md:translate-y-12"
          >
            <div className="space-y-6">
              <div className="w-12 h-12 bg-[#8a2424]/10 flex items-center justify-center border border-[#8a2424]/20 group-hover:bg-[#8a2424]/20 group-hover:border-[#8a2424]/40 transition-all duration-500">
                <Sparkles className="w-5 h-5 text-[#d97036]" />
              </div>
              <h3 className="font-serif text-2xl lg:text-3xl text-[#1c110e] font-light">
                {t.philosophy.techniqueTitle}
              </h3>
              <p className="font-sans text-sm lg:text-base text-[#5a4b46] leading-relaxed font-light">
                {t.philosophy.techniqueText}
              </p>
            </div>
            <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-transparent group-hover:border-[#d97036] transition-all duration-300" />
          </div>
        </div>

        {/* Ambient background section image - dramatic visual offset */}
        <div className="mt-40 md:grid md:grid-cols-12 md:gap-8 items-center">
          <div className="md:col-span-7 overflow-hidden bg-[#1f1612] border border-[#b58e70]/20 h-[400px] sm:h-[550px] flex items-center justify-center">
            <img
              src={`${process.env.PUBLIC_URL}/images/dialog.jpg`}
              alt="Walter & Regina – Intimate Tango Connection"
              className="max-w-full max-h-full object-contain hover:scale-105 duration-1000 transition-transform"
            />
          </div>
          <div className="md:col-span-5 space-y-6 mt-8 md:mt-0 md:pl-8">
            <h4 className="font-serif text-2xl text-[#1c110e] font-light">
              {t.philosophy.dialogueTitle}
            </h4>
            <p className="font-sans text-sm lg:text-base text-[#5a4b46] leading-relaxed font-light">
              {t.philosophy.dialogueText}
            </p>
          </div>
        </div>

      </div>
    </section>
  );
};
